"use client";

import { useState } from "react";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { descalePence, formatCurrency, formatPercent, isPenceCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { EarningsReactionPoint, EarningsReactionStats } from "@/lib/earnings-insights/reaction-tracker";

/** Reaktions-Tracker platzsparender (Feedback-Runde 16 Punkt 11, 2026-07-22): bisher
 * unbegrenzte Zeilen-Liste (bis zu ~12 Quartale) nach der kompakten 4er-Statistik-Zeile -
 * zeigt jetzt nur die juengsten Zeilen, Rest per "Mehr anzeigen" (gleiche Konvention wie
 * components/profile/company-description.tsx). */
const DEFAULT_VISIBLE_ROWS = 5;

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("de-DE", { year: "2-digit", month: "short" });
}

function formatFullDate(iso: string): string {
  return new Date(iso).toLocaleDateString("de-DE", { year: "numeric", month: "long", day: "2-digit" });
}

/** Divergierender Mini-Balken um die Nullachse statt eines immer links verankerten,
 * nur ueber die Farbe signierten Balkens (Erik, Runde 8 Punkt 20: "unuebersichtlich,
 * moderner/schneller erfassbar") - gleiches Muster wie DeviationBar in
 * rebalancing-card.tsx, macht "nach oben" vs. "nach unten" sofort an der Balken-
 * Richtung erkennbar statt nur an der Farbe ablesbar. */
function ReactionBar({ reactionPct, maxAbsReactionPct }: { reactionPct: number; maxAbsReactionPct: number }) {
  const widthPercent = maxAbsReactionPct > 0 ? (Math.abs(reactionPct) / maxAbsReactionPct) * 50 : 0;
  return (
    <div className="relative h-2 flex-1 rounded-full bg-hairline">
      <div className="absolute inset-y-0 left-1/2 w-px bg-border" />
      <div
        className={cn(
          "absolute inset-y-0 rounded-full",
          reactionPct >= 0 ? "left-1/2 bg-positive" : "right-1/2 bg-negative",
        )}
        style={{ width: `${widthPercent}%` }}
      />
    </div>
  );
}

/** Drill-down fuer eine einzelne Reaktion (Erik: "optionale Moeglichkeit, an
 * passenden Stellen tiefer in einzelne Reaktionen einzutauchen") - Hover-Tooltip
 * statt eines eigenen Dialogs/einer eigenen Seite, zeigt die zugrunde liegenden
 * Vorher-/Nachher-Kurse samt Datum, aus denen sich reactionPct ergibt. */
function ReactionRow({
  point,
  maxAbsReactionPct,
  currency,
}: {
  point: EarningsReactionPoint;
  maxAbsReactionPct: number;
  /** Native Notierungswaehrung - fuer Pence-Waehrungen (GBp/GBX) werden die
   * Vorher-/Nachher-Kurse im Tooltip auf echtes GBP herunterskaliert (siehe
   * "Bekannte Bugs" -> Pence-notierte LSE-Aktien in backlog.md), reactionPct selbst
   * ist als Verhaeltnis bereits skalenunabhaengig korrekt. */
  currency: string | undefined;
}) {
  const displayCurrency = isPenceCurrency(currency) ? "GBP" : currency;
  const priceBefore = descalePence(point.priceBefore, currency);
  const priceAfter = descalePence(point.priceAfter, currency);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex w-full items-center gap-3 border-b border-hairline py-2 text-xs last:border-0">
          <span className="w-16 shrink-0 text-muted-foreground">{formatShortDate(point.filingDate)}</span>
          <ReactionBar reactionPct={point.reactionPct} maxAbsReactionPct={maxAbsReactionPct} />
          <span
            className={cn(
              "w-16 shrink-0 text-right font-tabular",
              point.reactionPct >= 0 ? "text-positive" : "text-negative",
            )}
          >
            {point.reactionPct >= 0 ? "+" : ""}
            {formatPercent(point.reactionPct)}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-64 text-xs">
        <p className="font-medium">Quartalszahlen am {formatFullDate(point.filingDate)}</p>
        <p className="mt-1">
          {formatFullDate(point.priceBeforeDate)}: {formatCurrency(priceBefore, displayCurrency)}
          <br />→ {formatFullDate(point.priceAfterDate)}: {formatCurrency(priceAfter, displayCurrency)}
        </p>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Earnings-Reaktions-Tracker (backlog.md -> D9) - wie stark der Kurs historisch auf
 * Quartalszahlen reagiert hat, rein deskriptive Statistik. Bewusst ohne Trefferquote
 * gegenüber Analystenschätzungen (siehe reaction-tracker.ts-Docstring für die
 * Datenlücke).
 */
export function EarningsReactionPanel({
  stats,
  currency,
}: {
  stats: EarningsReactionStats;
  currency?: string;
}) {
  const [showAll, setShowAll] = useState(false);
  const maxAbsReactionPct = Math.max(0.01, ...stats.points.map((p) => Math.abs(p.reactionPct)));
  const orderedPoints = stats.points.slice().reverse();
  const visiblePoints = showAll ? orderedPoints : orderedPoints.slice(0, DEFAULT_VISIBLE_ROWS);
  const hasMore = orderedPoints.length > DEFAULT_VISIBLE_ROWS;
  return (
    <div className="flex flex-col gap-3">
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-secondary">
        Earnings-Reaktions-Tracker
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Erklärung: Earnings-Reaktions-Tracker"
              className="text-muted-foreground hover:text-ink"
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-72">
            Kursbewegung vom letzten Schlusskurs vor bis zum ersten Schlusskurs nach jeder
            bekannten Quartalsergebnis-Einreichung (SEC 8-K, Item 2.02) - basierend auf den
            in SEC-Einreichungen verfügbaren Terminen (typischerweise die letzten 1-3
            Jahre). Rein historische Statistik, keine Prognose für künftige Termine.
          </TooltipContent>
        </Tooltip>
      </span>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div>
          <span className="block text-xs text-muted-foreground">Ø Bewegung</span>
          <span className={cn("font-tabular text-xl font-semibold", stats.averageReactionPct >= 0 ? "text-positive" : "text-negative")}>
            {stats.averageReactionPct >= 0 ? "+" : ""}
            {formatPercent(stats.averageReactionPct)}
          </span>
        </div>
        <div>
          <span className="block text-xs text-muted-foreground">Ø Ausschlag (absolut)</span>
          <span className="font-tabular text-xl font-semibold text-ink">{formatPercent(stats.averageAbsReactionPct)}</span>
        </div>
        <div>
          <span className="block text-xs text-muted-foreground">Positiv reagiert</span>
          <span className="font-tabular text-xl font-semibold text-ink">
            {stats.positiveCount} / {stats.points.length}
          </span>
        </div>
        <div>
          <span className="block text-xs text-muted-foreground">Negativ reagiert</span>
          <span className="font-tabular text-xl font-semibold text-ink">
            {stats.negativeCount} / {stats.points.length}
          </span>
        </div>
      </div>

      <div className="flex flex-col">
        {visiblePoints.map((p) => (
          <ReactionRow key={p.filingDate} point={p} maxAbsReactionPct={maxAbsReactionPct} currency={currency} />
        ))}
      </div>
      {hasMore && (
        <button
          type="button"
          onClick={() => setShowAll((prev) => !prev)}
          className="w-fit text-sm font-medium text-secondary hover:text-ink"
        >
          {showAll ? "Weniger anzeigen" : `Alle ${orderedPoints.length} anzeigen`}
        </button>
      )}
    </div>
  );
}
